import { clsx } from "clsx";
import { useMemo } from "react";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { useSectionVisibility } from "@/hooks/useSectionVisibility";
import { useTimeTracking } from "@/hooks/useTimeTracking";
import { renderSection } from "@/registry";
import type { HeadlineVariantProps, PostSubmitContext } from "@/types/config";
import type { PageConfigData } from "@/types/page";

export interface PageRendererProps {
  config: PageConfigData;
  headlineVariant?: HeadlineVariantProps | null;
  postSubmit?: PostSubmitContext | null;
}

export function PageRenderer({ config, headlineVariant, postSubmit }: PageRendererProps) {
  const { theme, presentation, sections } = config;

  // Kit pages (ADR-0076) render under the `.guide-page` shell: premium tokens
  // + Onest via guide-system.css (loaded by the kit sections' lazy chunks),
  // sections own their vertical rhythm (no space-y), no DaisyUI bg-bg.
  const isKit = theme?.variant === "kit";
  const themeClasses = !isKit && theme ? getThemeClasses(theme) : "";
  const spacingClass = !isKit && theme?.spacing ? getSpacingClass(theme.spacing) : "";

  const sectionIds = useMemo(
    () =>
      sections
        .filter((section) => "id" in section && section.id)
        .map((section) => ("id" in section ? section.id : undefined))
        .filter((id): id is string => !!id),
    [sections],
  );

  useScrollDepth();
  useSectionVisibility(sectionIds);
  useTimeTracking();

  return (
    <div
      className={clsx(isKit ? "guide-page" : "bg-bg", "min-h-screen", themeClasses, spacingClass)}
      data-presentation={presentation}
      data-component="PageRenderer"
    >
      {sections.map((section, index) =>
        renderSection(section, index, { headlineVariant, postSubmit }),
      )}
    </div>
  );
}

function getThemeClasses(theme: { variant?: string; mode?: string }) {
  const classes = [];

  if (theme.variant && theme.variant !== "default") {
    classes.push(`theme-${theme.variant}`);
  }

  if (theme.mode === "dark") {
    classes.push("dark");
  }

  return classes.join(" ");
}

function getSpacingClass(spacing: string): string {
  switch (spacing) {
    case "compact":
      return "space-y-8";
    case "relaxed":
      return "space-y-24";
    case "none":
      return "space-y-0";
    default:
      return "space-y-16";
  }
}

PageRenderer.displayName = "PageRenderer";

export default PageRenderer;
